import type { UsageHourlyBucket, UsageHourlyResponse } from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Line } from '../../charts/index.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import {
  formatTokens,
  formatUsdMicrosOrCentsAsCnyMetered,
  parseTimestampMs,
  usdMicrosFromCentsFallback,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Panel, buttonClass } from '../../ui/index.ts';
import {
  daySpanInclusive,
  localDayString,
  rollingDayRange,
  shiftLocalDay,
  todayLocalDay,
} from '../usage/format.ts';

/**
 * Seven-day shape, two series — BOTH folded from the SAME hourly
 * response.
 *
 * The trade-off, stated because it reverses an earlier decision: the
 * spend series used to come from `/v1/usage/daily` so that each panel
 * owned its own query and one failing series could not blank the other.
 * The two endpoints do not agree on where a day starts, so the same x
 * position carried two points from two different days (#20). Sharing
 * one query loses the failure isolation between the two panels and buys
 * a single definition of "a day" for both lines. That was the explicit
 * ask: do not let two series each interpret the calendar.
 *
 * "A day" here is a LOCAL calendar day — the same one the date pickers
 * on `/usage` compute, the same one `/requests` prints timestamps in.
 * The window comes from `rollingDayRange` in `usage/format.ts` rather
 * than from private math, because that module is the app's only home
 * for calendar ranges.
 *
 * The chart is never the sole carrier of a number — the per-point
 * tooltips spell out date and value, and the trailing partial day is
 * called out in prose as well as by fade (DESIGN.md §9).
 */

/** Local calendar days the row covers, ending on today. */
const TREND_DAYS = 7;

/**
 * Days of hourly data requested — one more than the row draws.
 *
 * `/v1/usage/hourly` serves a ROLLING window of `days * 24` hours
 * ending at `now`, not a calendar range. The span from the first drawn
 * day's local midnight back from `now` is 6 calendar days plus however
 * much of today has elapsed, so on a plain 24-hour day `7 * 24h` would
 * just barely cover it — the UTC offset does not matter, because the
 * elapsed part of today is under 24h in every zone.
 *
 * The margin is there for the cases where that arithmetic stops
 * holding: a DST fall-back day is 25 hours long, so 6 calendar days can
 * exceed `6 * 24h`; and `today` is chosen from the CLIENT clock while
 * `now` is the SERVER's, so any skew between them eats into the same
 * budget. One extra day covers both without a second request.
 */
export const TREND_QUERY_DAYS = TREND_DAYS + 1;

interface TrendDay {
  readonly date: string;
  readonly requests: number;
  readonly costUsdMicros: number;
}

/**
 * Hourly buckets → one row per LOCAL calendar day in `[from, to]`, zero
 * where the server returned nothing.
 *
 * Zero-fill is the point: SQL `GROUP BY` emits no row for an hour with
 * no traffic, so a day with no traffic at all used to vanish from the
 * axis entirely and the line joined the days either side of it as if
 * they were adjacent (#20). Both series are filled, not just requests.
 *
 * A bucket belongs to the local day its START falls in. For whole-hour
 * offsets (Asia/Shanghai among them) that is exact. At `+05:30` or
 * `+05:45` a UTC hour straddles two local days and this attributes all
 * of it to the first — an approximation the hourly endpoint's own
 * resolution cannot avoid, recorded here rather than hidden. The same
 * rounding costs the first drawn day its opening half hour and gives
 * the last day a half hour of the next one; at most one bucket moves at
 * each boundary.
 *
 * `bucket.hour` is parsed with `parseTimestampMs`, never `new Date()`:
 * the server sometimes emits a zone-less `YYYY-MM-DD HH:MM:SS`, which
 * `new Date()` would read as LOCAL and so shift by the whole UTC
 * offset — silently reintroducing the very misplacement this function
 * exists to remove. That helper is also what `/requests` and the
 * activity table below use, so all three agree on the same instant.
 *
 * Buckets outside the window (the extra day `TREND_QUERY_DAYS` pulls)
 * are dropped: they are there to complete the first day, not to extend
 * the axis. An unparsable stamp is dropped rather than trusted.
 */
export function rollupHourlyToLocalDays(
  buckets: readonly UsageHourlyBucket[],
  window: { readonly from: string; readonly to: string },
): readonly TrendDay[] {
  const totals = new Map<string, { requests: number; costUsdMicros: number }>();
  for (const bucket of buckets) {
    const ms = parseTimestampMs(bucket.hour);
    if (!Number.isFinite(ms)) continue;
    const day = localDayString(new Date(ms));
    if (day < window.from || day > window.to) continue;
    const current = totals.get(day) ?? { requests: 0, costUsdMicros: 0 };
    totals.set(day, {
      requests: current.requests + bucket.requests,
      costUsdMicros:
        current.costUsdMicros +
        usdMicrosFromCentsFallback(bucket.costUsdMicros, bucket.costUsdCents),
    });
  }
  return Array.from({ length: daySpanInclusive(window.from, window.to) }, (_, offset) => {
    const date = shiftLocalDay(window.from, offset);
    const totalsForDay = totals.get(date);
    return {
      date,
      requests: totalsForDay?.requests ?? 0,
      costUsdMicros: totalsForDay?.costUsdMicros ?? 0,
    };
  });
}

function UsageLink({ testId }: { readonly testId: string }): JSX.Element {
  const t = useT();
  return (
    <Link to="/usage" className={buttonClass('ghost', 'sm')} data-testid={testId}>
      {t('dashboard.trend.view_usage')}
    </Link>
  );
}

export function TrendRow(props: {
  readonly hourlyQ: UseQueryResult<UsageHourlyResponse>;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const hourlyBuckets = props.hourlyQ.data?.buckets;
  const days = useMemo(
    () =>
      rollupHourlyToLocalDays(hourlyBuckets ?? [], rollingDayRange(todayLocalDay(), TREND_DAYS)),
    [hourlyBuckets],
  );
  // Only the last day is partial, and it always is: it is today, and
  // today is still being written. Earlier days in the window closed
  // hours ago.
  const incomplete = days.map((_, index) => index === days.length - 1);
  const tickLabels = days.map((day) => day.date);
  const isEmpty = (hourlyBuckets ?? []).length === 0;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="dashboard-trends">
      <Panel
        caption={t('dashboard.trend.requests.caption')}
        padded
        testId="dashboard-trend-requests"
        actions={<UsageLink testId="dashboard-trend-requests-link" />}
      >
        {props.hourlyQ.isLoading ? (
          <Skeleton className="h-24" />
        ) : props.hourlyQ.isError ? (
          <PanelError
            message={t('dashboard.trend.error')}
            retryLabel={t('dashboard.retry')}
            onRetry={() => {
              void props.hourlyQ.refetch();
            }}
            testId="dashboard-trend-requests-error"
          />
        ) : isEmpty ? (
          <p className="m-0 text-caption text-ink-low">{t('dashboard.trend.empty')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <Line
              points={days.map((day) => day.requests)}
              labels={days.map((day) => `${day.date} · ${formatTokens(day.requests)}`)}
              incomplete={incomplete}
              incompleteLabel={t('dashboard.trend.incomplete')}
              tickLabels={tickLabels}
              strokeClassName="stroke-forest"
              dotClassName="fill-forest"
              ariaLabel={t('dashboard.trend.requests.caption')}
              testId="dashboard-trend-requests-chart"
            />
            <PartialDayNote />
          </div>
        )}
      </Panel>
      <Panel
        caption={t('dashboard.trend.spend.caption')}
        padded
        testId="dashboard-trend-spend"
        actions={<UsageLink testId="dashboard-trend-spend-link" />}
      >
        {props.hourlyQ.isLoading ? (
          <Skeleton className="h-24" />
        ) : props.hourlyQ.isError ? (
          <PanelError
            message={t('dashboard.trend.error')}
            retryLabel={t('dashboard.retry')}
            onRetry={() => {
              void props.hourlyQ.refetch();
            }}
            testId="dashboard-trend-spend-error"
          />
        ) : isEmpty ? (
          <p className="m-0 text-caption text-ink-low">{t('dashboard.trend.empty')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <Line
              points={days.map((day) => day.costUsdMicros)}
              labels={days.map(
                (day) =>
                  `${day.date} · ${formatUsdMicrosOrCentsAsCnyMetered(
                    day.costUsdMicros,
                    0,
                    props.cnyPerUsd,
                  )}`,
              )}
              incomplete={incomplete}
              incompleteLabel={t('dashboard.trend.incomplete')}
              tickLabels={tickLabels}
              strokeClassName="stroke-moss"
              dotClassName="fill-moss"
              ariaLabel={t('dashboard.trend.spend.caption')}
              testId="dashboard-trend-spend-chart"
            />
            <PartialDayNote />
          </div>
        )}
      </Panel>
    </div>
  );
}

/** Visible prose for the fade — a chart may not encode this by opacity alone. */
function PartialDayNote(): JSX.Element {
  const t = useT();
  return <p className="m-0 text-caption text-ink-low">{t('dashboard.trend.incomplete_note')}</p>;
}
